import React, { useContext, useEffect, useMemo } from 'react';

import moment from 'moment';
import PropTypes from 'prop-types';
import { Button, Col, DatePicker, Form, Input, Row, Select, Spin } from 'antd';
import { useIntl } from 'react-intl';

import Divider from 'components/Divider';
import IntlMessages from 'util/IntlMessages';
import Title from 'components/BoxContainer/components/Title';
import DebounceCloudFetchSelect from '@src/containers/DebounceCloudFetchSelect';
import { LAYOUT_BY_DEFAULT } from 'packages/dashboard/screens/EventsDashboard/constants';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import UsersTable from './UsersTable';
import { DT_PROCESS_TYPES } from '../../constants';
import AlertFiltersModal from './AlertFiltersModal';
import useTimeTrackerFilters from './hooks/useTimeTrackerFilters';
import { ReportContext } from '../contexts/ReportContext';
import { DateUtil } from '@src/packages/utils/dates';
import { hasGroupFilterValue } from '../../utils/usersSelectedParams';

const { Item } = Form;

export const applyPeriodPresetToForm = (form, value) => {
  form.setFieldsValue({
    period: value,
    startDate: undefined,
    endDate: undefined,
  });
};

const TimeTrackerFilters = ({ formName, form, onSubmit, refresh, onHashSubmit }) => {
  const intl = useIntl();

  const {
    alertType,
    setAlertType,
    currentPage,
    loading,
    isSelfMode,
    supervisorThirdParty,
    groupUsers,
    groupUsersLoading,
    filtersData,
    periods,
    groupUserColumns,
    workShiftsLoading,
    regionsLoading,
    premisesLoading,
    userJobsLoading,
    companiesLoading,
    userIdValue,
    hashValue,
    onChangeUsers,
    onChangePagination,
    onSave,
    filterGroupUsers,
    setProcessType,
    QUERY_DEBOUNCE_SELECT,
    getSelectParsedData,
    getUserSelectLabel,
    GROU_USERS_PER_PAGE,
    onClearGroupFilters,
  } = useTimeTrackerFilters({ form, onSubmit, refresh, onHashSubmit });

  const { getSelectedIndividualUser, setSelectedIndividualUser, selectedIndividualUser } =
    useContext(ReportContext);

  const userIdWatch = Form.useWatch('userId', form);

  const selectedUserLabel = useMemo(() => {
    if (!userIdValue) return '';

    const user = selectedIndividualUser ?? getSelectedIndividualUser(userIdValue);
    return getUserSelectLabel(user);
  }, [userIdValue, selectedIndividualUser, getSelectedIndividualUser, getUserSelectLabel]);
  const usersSelectedWatch = Form.useWatch('usersSelected', form);

  useEffect(() => {
    if (isSelfMode) return;

    const hasUsersSelected = Array.isArray(usersSelectedWatch) && usersSelectedWatch.length > 0;
    if (!hasUsersSelected) return;

    if (form.getFieldValue('userId') !== '') {
      setSelectedIndividualUser(null);
      form.setFieldsValue({ userId: '' });
    }
  }, [usersSelectedWatch, isSelfMode, form, setSelectedIndividualUser]);

  useEffect(() => {
    if (isSelfMode || !userIdWatch) return;

    const selected = form.getFieldValue('usersSelected');
    const needsClear = !Array.isArray(selected) || selected.length > 0;
    if (needsClear) {
      form.setFieldsValue({ usersSelected: [] });
    }
  }, [userIdWatch, isSelfMode, form]);

  return (
    <>
      <Form
        form={form}
        layout={LAYOUT_BY_DEFAULT}
        name={formName}
        onFinish={() => onSave()}
        onFieldsChange={changedFields => {
          const groupFields = ['workJourneys', 'shift', 'region', 'premises', 'jobs', 'companies'];
          if (changedFields.some(field => groupFields.includes(field.name?.[0]))) {
            filterGroupUsers();
          }
        }}
      >
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Title.SubTitle value={<IntlMessages id="work.direction.filter.period.range.date" />} />
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.default.period" />} />
            <Item name="period">
              <Select
                data-testid="period-select"
                onChange={value => applyPeriodPresetToForm(form, value)}
                options={periods}
                className="gx-w-100"
                allowClear
              />
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.start.date" />} />
            <Item name="startDate">
              <DatePicker
                onChange={() => {
                  const period = form.getFieldValue('period');
                  if (period !== null) {
                    form.setFieldsValue({ period: null });
                  }
                }}
                format={DateUtil.DEFAULT_DT_DATE_FORMAT}
                getPopupContainer={trigger => trigger.parentNode}
                allowClear={true}
                className="gx-w-100"
                disabledDate={current => current && current > moment.now()}
                inputReadOnly
              />
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.end.date" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.startDate !== curr.startDate}>
              {({ getFieldValue }) => (
                <Item name="endDate">
                  <DatePicker
                    onChange={() => {
                      const period = form.getFieldValue('period');
                      if (period !== null) {
                        form.setFieldsValue({ period: null });
                      }
                    }}
                    format={DateUtil.DEFAULT_DT_DATE_FORMAT}
                    getPopupContainer={trigger => trigger.parentNode}
                    allowClear={true}
                    className="gx-w-100"
                    disabledDate={current =>
                      (current &&
                        current <
                          moment(getFieldValue('startDate')?.format('YYYY-MM-DD'), 'YYYY-MM-DD')) ||
                      current > moment.now()
                    }
                    inputReadOnly
                  />
                </Item>
              )}
            </Item>
          </Col>
        </Row>
        {isSelfMode ? (
          <Row gutter={[8, 8]}>
            <Col
              xs={24}
              md={8}
              lg={{ span: 8, push: 16 }}
              xl={{ span: 5, push: 19 }}
              xxl={{ span: 4, push: 20 }}
            >
              <Button
                onClick={() => setProcessType(DT_PROCESS_TYPES.INDIVIDUAL)}
                htmlType="submit"
                className="gx-mb-0 gx-ml-1"
                type="primary"
                disabled={!userIdValue}
                block
              >
                <IntlMessages id="work.direction.generate.single.report" />
              </Button>
            </Col>
          </Row>
        ) : (
          <Divider />
        )}
        <Row hidden={isSelfMode} justify="space-between" gutter={[8, 8]}>
          <Col span={24}>
            <Title.SubTitle value={<IntlMessages id="work.direction.filter.individual.search" />} />
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.search.user" />} />
            <Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.workJourneys !== curr.workJourneys ||
                prev.shift !== curr.shift ||
                prev.region !== curr.region ||
                prev.premises !== curr.premises ||
                prev.jobs !== curr.jobs ||
                prev.companies !== curr.companies
              }
            >
              {({ getFieldValue }) => (
                <Item name="userId" data-testid="user-id-input">
                  <DebounceCloudFetchSelect
                    selectedLabel={selectedUserLabel}
                    placeholder={intl.formatMessage({
                      id: 'activeUserDashboard.findUser.placeholder',
                    })}
                    disabled={
                      isSelfMode ||
                      hasGroupFilterValue(getFieldValue('workJourneys')) ||
                      hasGroupFilterValue(getFieldValue('shift')) ||
                      hasGroupFilterValue(getFieldValue('region')) ||
                      hasGroupFilterValue(getFieldValue('premises')) ||
                      hasGroupFilterValue(getFieldValue('jobs')) ||
                      hasGroupFilterValue(getFieldValue('companies'))
                    }
                    emptyPlaceholder="timeTracker.groupUsersTable.emptyState"
                    op={API_OPERATIONS.GENERIC_FIND}
                    params={QUERY_DEBOUNCE_SELECT.userId}
                    fieldToSearch="textSearch"
                    onChange={val => {
                      const user = getSelectedIndividualUser(val);
                      if (!user) return;
                      form.setFieldsValue({ userId: user.id, usersSelected: [] });
                      setSelectedIndividualUser(user);
                    }}
                    parseData={getSelectParsedData}
                    searchKey={['firstName', 'lastName', 'idCard']}
                    perPage={1}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <div className="gx-flex-column gx-justify-content-center">
            <Button
              onClick={() => setProcessType(DT_PROCESS_TYPES.INDIVIDUAL)}
              htmlType="submit"
              className="gx-mb-0 gx-ml-1"
              type="primary"
              disabled={!userIdValue}
            >
              <IntlMessages id="work.direction.generate.single.report" />
            </Button>
          </div>
        </Row>

        {!isSelfMode && <Divider />}
        <Row hidden={isSelfMode} className="gx-pb-4" gutter={[8, 8]}>
          <Col span={24}>
            <Title.SubTitle value={<IntlMessages id="work.direction.filter.group.search" />} />
          </Col>
          <Col span={24}>
            <Item noStyle shouldUpdate={(prev, curr) => prev.usersSelected !== curr.usersSelected}>
              {({ getFieldValue }) => (
                <Item name="usersSelected">
                  <UsersTable
                    usersSelected={getFieldValue('usersSelected') || []}
                    columns={groupUserColumns}
                    data={groupUsers.users}
                    handleUsers={onChangeUsers}
                    loading={groupUsersLoading || loading}
                    onPagination={onChangePagination}
                    pagination={{
                      pageSize: GROU_USERS_PER_PAGE,
                      onChange: onChangePagination,
                      total: groupUsers?.total,
                      responsive: true,
                      showSizeChanger: false,
                      current: currentPage,
                    }}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.working.day" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.userId !== curr.userId}>
              {({ getFieldValue }) => (
                <Item name="workJourneys">
                  <Select
                    disabled={!!getFieldValue('userId')}
                    data-testid="work-journeys-select"
                    defaultValue={[]}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    options={filtersData.workJourneys}
                    getPopupContainer={trigger => trigger.parentNode}
                    className="gx-w-100"
                    mode="multiple"
                    onChange={value => {
                      form.setFieldsValue({ workJourneys: value, shift: [] });
                    }}
                    onClear={() => {
                      onClearGroupFilters('workJourneys');
                      onClearGroupFilters('shift');
                    }}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.shifts" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.userId !== curr.userId}>
              {({ getFieldValue }) => (
                <Item name="shift">
                  <Select
                    disabled={!!getFieldValue('userId')}
                    data-testid="shifts-select"
                    showSearch
                    optionFilterProp="label"
                    loading={workShiftsLoading}
                    allowClear
                    options={filtersData.shifts}
                    className="gx-w-100"
                    mode="multiple"
                    getPopupContainer={trigger => trigger.parentNode}
                    notFoundContent={
                      workShiftsLoading ? (
                        <Spin size="small" />
                      ) : (
                        <IntlMessages id="work.direction.filter.shifts.emptyState" />
                      )
                    }
                  />
                </Item>
              )}
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.region" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.userId !== curr.userId}>
              {({ getFieldValue }) => (
                <Item name="region">
                  <Select
                    data-testid="regions-select"
                    disabled={!!getFieldValue('userId')}
                    onChange={value => {
                      form.setFieldsValue({ region: value, premises: [] });
                    }}
                    loading={regionsLoading}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    options={filtersData.regions}
                    className="gx-w-100"
                    mode="multiple"
                    getPopupContainer={trigger => trigger.parentNode}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.premises" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.userId !== curr.userId}>
              {({ getFieldValue }) => (
                <Item name="premises">
                  <Select
                    disabled={!!getFieldValue('userId')}
                    data-testid="premises-select"
                    loading={premisesLoading}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    options={filtersData.premises}
                    className="gx-w-100"
                    mode="multiple"
                    getPopupContainer={trigger => trigger.parentNode}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.job.or.function" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.userId !== curr.userId}>
              {({ getFieldValue }) => (
                <Item name="jobs">
                  <Select
                    disabled={!!getFieldValue('userId')}
                    data-testid="jobs-select"
                    loading={userJobsLoading}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    options={filtersData.jobs}
                    className="gx-w-100"
                    mode="multiple"
                    getPopupContainer={trigger => trigger.parentNode}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.filter.contractor" />} />
            <Item noStyle shouldUpdate={(prev, curr) => prev.userId !== curr.userId}>
              {({ getFieldValue }) => (
                <Item name="companies">
                  <Select
                    disabled={supervisorThirdParty || !!getFieldValue('userId')}
                    data-testid="companies-select"
                    loading={companiesLoading}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    options={filtersData.companies}
                    className="gx-w-100"
                    getPopupContainer={trigger => trigger.parentNode}
                  />
                </Item>
              )}
            </Item>
          </Col>
          <div className="gx-w-100 gx-flex-row gx-justify-content-end">
            <Item noStyle shouldUpdate={(prev, curr) => prev.usersSelected !== curr.usersSelected}>
              {({ getFieldValue }) => (
                <Button
                  data-testid="group-search-button"
                  htmlType="submit"
                  onClick={() => setProcessType(DT_PROCESS_TYPES.GROUP)}
                  type="primary"
                  className="gx-mb-0 gx-ml-1"
                  disabled={!!getFieldValue('userId')}
                >
                  <IntlMessages id="work.direction.generate.group.report" />
                </Button>
              )}
            </Item>
          </div>
        </Row>
        <Divider />

        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Title.SubTitle value={<IntlMessages id="work.direction.hash" />} />
          </Col>
          <Col xs={24} sm={8} md={8} lg={8}>
            <Title.LabelForm value={<IntlMessages id="work.direction.enter.hash" />} />
            <Item name="hash">
              <Input
                maxLength={64}
                placeholder={intl.formatMessage({
                  id: 'work.direction.enter.hash.placeholder',
                })}
              />
            </Item>
          </Col>
          <Col
            xs={24}
            sm={8}
            md={8}
            lg={{ span: 8, push: 8 }}
            xl={{ span: 5, push: 11 }}
            xxl={{ span: 4, push: 12 }}
          >
            <Title.LabelForm value=" " />
            <Button
              onClick={() => setProcessType(DT_PROCESS_TYPES.HASH)}
              htmlType="submit"
              className="gx-mb-0 gx-ml-1"
              type="primary"
              disabled={!hashValue}
              block
            >
              <IntlMessages id="button.generate.hash.report" />
            </Button>
          </Col>
        </Row>
      </Form>
      <AlertFiltersModal
        alertType={alertType}
        onContinue={() => onSave(true)}
        onClose={() => setAlertType(null)}
      />
    </>
  );
};

export default React.memo(TimeTrackerFilters);

TimeTrackerFilters.propTypes = {
  formName: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onHashSubmit: PropTypes.func,
  refresh: PropTypes.bool,
};
